import { useQuery } from "@tanstack/react-query";
import {
	AlertCircle,
	Building01,
	Building07,
	Edit05,
	GitMerge,
	Mail01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field/detail-field";
import { Input } from "@/components/base/input/input";
import { TabHeader } from "@/components/base/tab-header";
import { EmptyValue } from "@/components/common";
import { useAppForm } from "@/components/form";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAuth } from "@/contexts/AuthProvider";
import type { AppTranslateFn } from "@/hooks/use-app-translation";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import { useUpdateUserInfo } from "@/services/user/user.mutations";
import type { User } from "@/types/auth.types";

// ============================================================================
// USER TYPE DETECTION
// ============================================================================

/**
 * Platform-admin signal matches ticket types / admin layout: ViewAllOrganizations.
 */
function useIsPlatformAdmin(): boolean {
	const viewAllOrgsQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);
	return viewAllOrgsQuery.data ?? false;
}

/**
 * Limited/SSO form when first-party auth is off, or when ViewAllOrganizations is true.
 */
function useIsThirdPartyAuth(user: User | null): boolean {
	const isPlatformAdmin = useIsPlatformAdmin();
	if (!user) return false;
	if (isPlatformAdmin) return true;
	return user.usingFirstPartyAuth === false;
}

// ============================================================================
// FORM SCHEMAS
// ============================================================================

/**
 * Limited form schema for:
 * - Platform administrators
 * - Organization users with 3rd party auth (SSO)
 *
 * Can only edit: displayName
 */
const createLimitedProfileFormSchema = (t: AppTranslateFn) =>
	z.object({
		displayName: z.string().min(1, t("common.validation.displayNameRequired")),
	});

type LimitedProfileFormValues = z.infer<
	ReturnType<typeof createLimitedProfileFormSchema>
>;

/**
 * Full form schema for:
 * - Organization users with 1st party auth
 *
 * Can edit: firstName, lastName, displayName
 */
const createFullProfileFormSchema = (t: AppTranslateFn) =>
	z.object({
		firstName: z.string().min(1, t("common.validation.firstNameRequired")),
		lastName: z.string().min(1, t("common.validation.lastNameRequired")),
		displayName: z.string().min(1, t("common.validation.displayNameRequired")),
	});

type FullProfileFormValues = z.infer<
	ReturnType<typeof createFullProfileFormSchema>
>;

// ============================================================================
// LIMITED PROFILE FORM COMPONENT
// ============================================================================

interface LimitedProfileFormProps {
	user: User;
	onCancel: () => void;
	onSuccess: () => void;
}

function LimitedProfileForm({
	user,
	onCancel,
	onSuccess,
}: LimitedProfileFormProps) {
	const { t } = useAppTranslation();
	const updateUserMutation = useUpdateUserInfo();
	const limitedSchema = useMemo(() => createLimitedProfileFormSchema(t), [t]);

	const form = useAppForm({
		defaultValues: {
			displayName: user.displayName || "",
		} as LimitedProfileFormValues,
		validators: {
			onChange: limitedSchema,
		},
		onSubmit: async ({ value }) => {
			await updateUserMutation.mutateAsync({
				userId: user.id,
				data: {
					displayName: value.displayName,
				},
			});
			onSuccess();
		},
	});

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
			className="space-y-4"
		>
			<DetailFieldGrid>
				<DetailFieldSection>
					{/* Display Name - Editable */}
					<form.Field name="displayName">
						{(field) => (
							<div className="flex flex-col gap-1">
								<Input
									type="text"
									label={t("userProfile.userDetails.displayName")}
									name={field.name}
									value={field.state.value}
									onChange={(value: string) => field.handleChange(value)}
									onBlur={field.handleBlur}
									placeholder={t(
										"userProfile.userDetails.placeholders.displayName",
									)}
									isRequired
									isInvalid={
										field.state.meta.isTouched &&
										field.state.meta.errors.length > 0
									}
								/>
								{field.state.meta.isTouched &&
									field.state.meta.errors.length > 0 && (
										<p className="text-sm text-error-600">
											{field.state.meta.errors[0]?.message}
										</p>
									)}
							</div>
						)}
					</form.Field>
				</DetailFieldSection>

				<DetailFieldSection>
					{/* Email - Read Only */}
					<DetailField
						label={t("userProfile.userInformation.email")}
						value={
							user.email ? (
								<div className="flex items-center gap-2">
									<Mail01 size={15} className="text-gray-400" />
									{user.email}
								</div>
							) : (
								<EmptyValue />
							)
						}
						copyable
						copyText={user.email}
						valueClassName="font-bold text-gray-900"
					/>
				</DetailFieldSection>
			</DetailFieldGrid>

			{/* Form Actions */}
			<div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
				<Button color="secondary" size="sm" onClick={onCancel}>
					{t("common.actions.cancel")}
				</Button>
				<form.Subscribe
					selector={(state) => [state.canSubmit, state.isSubmitting]}
				>
					{([canSubmit, isSubmitting]) => (
						<Button
							type="submit"
							color="primary"
							size="sm"
							disabled={!canSubmit}
							isLoading={isSubmitting || updateUserMutation.isPending}
						>
							{t("common.actions.save")}
						</Button>
					)}
				</form.Subscribe>
			</div>
		</form>
	);
}

// ============================================================================
// FULL PROFILE FORM COMPONENT
// ============================================================================

interface FullProfileFormProps {
	user: User;
	onCancel: () => void;
	onSuccess: () => void;
}

function FullProfileForm({ user, onCancel, onSuccess }: FullProfileFormProps) {
	const { t } = useAppTranslation();
	const updateUserMutation = useUpdateUserInfo();
	const fullSchema = useMemo(() => createFullProfileFormSchema(t), [t]);

	const form = useAppForm({
		defaultValues: {
			firstName: user.firstName || "",
			lastName: user.lastName || "",
			displayName: user.displayName || "",
		} as FullProfileFormValues,
		validators: {
			onChange: fullSchema,
		},
		onSubmit: async ({ value }) => {
			await updateUserMutation.mutateAsync({
				userId: user.id,
				data: {
					firstName: value.firstName,
					lastName: value.lastName,
					displayName: value.displayName,
				},
			});
			onSuccess();
		},
	});

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
			className="space-y-4"
		>
			<DetailFieldGrid>
				<DetailFieldSection>
					{/* First Name - Editable */}
					<form.Field name="firstName">
						{(field) => (
							<div className="flex flex-col gap-1">
								<Input
									type="text"
									label={t("userProfile.userDetails.firstName")}
									name={field.name}
									value={field.state.value}
									onChange={(value: string) => field.handleChange(value)}
									onBlur={field.handleBlur}
									placeholder={t(
										"userProfile.userDetails.placeholders.firstName",
									)}
									isRequired
									isInvalid={
										field.state.meta.isTouched &&
										field.state.meta.errors.length > 0
									}
								/>
								{field.state.meta.isTouched &&
									field.state.meta.errors.length > 0 && (
										<p className="text-sm text-error-600">
											{field.state.meta.errors[0]?.message}
										</p>
									)}
							</div>
						)}
					</form.Field>

					{/* Display Name - Editable */}
					<form.Field name="displayName">
						{(field) => (
							<div className="flex flex-col gap-1">
								<Input
									type="text"
									label={t("userProfile.userDetails.displayName")}
									name={field.name}
									value={field.state.value}
									onChange={(value: string) => field.handleChange(value)}
									onBlur={field.handleBlur}
									placeholder={t(
										"userProfile.userDetails.placeholders.displayName",
									)}
									isRequired
									isInvalid={
										field.state.meta.isTouched &&
										field.state.meta.errors.length > 0
									}
								/>
								{field.state.meta.isTouched &&
									field.state.meta.errors.length > 0 && (
										<p className="text-sm text-error-600">
											{field.state.meta.errors[0]?.message}
										</p>
									)}
							</div>
						)}
					</form.Field>
				</DetailFieldSection>

				<DetailFieldSection>
					{/* Last Name - Editable */}
					<form.Field name="lastName">
						{(field) => (
							<div className="flex flex-col gap-1">
								<Input
									type="text"
									label={t("userProfile.userDetails.lastName")}
									name={field.name}
									value={field.state.value}
									onChange={(value: string) => field.handleChange(value)}
									onBlur={field.handleBlur}
									placeholder={t(
										"userProfile.userDetails.placeholders.lastName",
									)}
									isRequired
									isInvalid={
										field.state.meta.isTouched &&
										field.state.meta.errors.length > 0
									}
								/>
								{field.state.meta.isTouched &&
									field.state.meta.errors.length > 0 && (
										<p className="text-sm text-error-600">
											{field.state.meta.errors[0]?.message}
										</p>
									)}
							</div>
						)}
					</form.Field>

					{/* Email - Read Only */}
					<DetailField
						label={t("userProfile.userInformation.email")}
						value={
							user.email ? (
								<div className="flex items-center gap-2">
									<Mail01 size={15} className="text-gray-400" />
									{user.email}
								</div>
							) : (
								<EmptyValue />
							)
						}
						copyable
						copyText={user.email}
						valueClassName="font-bold text-gray-900"
					/>
				</DetailFieldSection>
			</DetailFieldGrid>

			{/* Form Actions */}
			<div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
				<Button color="secondary" size="sm" onClick={onCancel}>
					{t("common.actions.cancel")}
				</Button>
				<form.Subscribe
					selector={(state) => [state.canSubmit, state.isSubmitting]}
				>
					{([canSubmit, isSubmitting]) => (
						<Button
							type="submit"
							color="primary"
							size="sm"
							disabled={!canSubmit}
							isLoading={isSubmitting || updateUserMutation.isPending}
						>
							{t("common.actions.save")}
						</Button>
					)}
				</form.Subscribe>
			</div>
		</form>
	);
}

// ============================================================================
// VIEW MODE COMPONENT
// ============================================================================

interface ViewModeProps {
	user: User;
	isPlatformAdmin: boolean;
	isThirdPartyAuth: boolean;
}

function ViewMode({ user, isPlatformAdmin, isThirdPartyAuth }: ViewModeProps) {
	const { t } = useAppTranslation();

	// Limited view for platform admins and 3rd party auth users
	const showLimitedFields = isPlatformAdmin || isThirdPartyAuth;

	return (
		<DetailFieldGrid>
			<DetailFieldSection>
				{/* First Name - Only show for full profile users */}
				{!showLimitedFields && (
					<DetailField
						label={t("userProfile.userDetails.firstName")}
						value={user.firstName || <EmptyValue />}
						valueClassName="font-bold text-gray-900"
					/>
				)}

				{/* Display Name */}
				<DetailField
					label={t("userProfile.userDetails.displayName")}
					value={user.displayName || <EmptyValue />}
					valueClassName="font-bold text-gray-900"
				/>

				{/* Email - Always read only */}
				<DetailField
					label={t("userProfile.userInformation.email")}
					value={
						user.email ? (
							<div className="flex items-center gap-2">
								<Mail01 size={15} className="text-gray-400" />
								{user.email}
							</div>
						) : (
							<EmptyValue />
						)
					}
					copyable
					copyText={user.email}
					valueClassName="font-bold text-gray-900"
				/>
			</DetailFieldSection>

			<DetailFieldSection>
				{/* Last Name - Only show for full profile users */}
				{!showLimitedFields && (
					<DetailField
						label={t("userProfile.userDetails.lastName")}
						value={user.lastName || <EmptyValue />}
						valueClassName="font-bold text-gray-900"
					/>
				)}

				{/* Belongs To - Only show for org users */}
				{!isPlatformAdmin &&
					(() => {
						const userAny = user as any;
						const belongsToName = userAny.belongsTo;
						let icon = null;

						// Determine icon based on entity type
						if (userAny.subContractor?.name) {
							icon = <GitMerge size={15} className="text-gray-400" />;
						} else if (userAny.subOrganization?.name) {
							icon = <Building01 size={15} className="text-gray-400" />;
						} else if (userAny.organization?.name) {
							icon = <Building07 size={15} className="text-gray-400" />;
						}

						return (
							<DetailField
								label={t("userProfile.userInformation.belongsTo")}
								value={
									belongsToName ? (
										<div className="flex items-center gap-2">
											{icon}
											{belongsToName}
										</div>
									) : (
										<EmptyValue />
									)
								}
								valueClassName="font-bold text-gray-900"
							/>
						);
					})()}
			</DetailFieldSection>
		</DetailFieldGrid>
	);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProfileDetailsContent() {
	const { t } = useAppTranslation();
	const { user, isLoading } = useAuth();
	const [isEditing, setIsEditing] = useState(false);

	const isPlatformAdmin = useIsPlatformAdmin();
	const isThirdPartyAuth = useIsThirdPartyAuth(user);

	const hasError = !user && !isLoading;

	// Determine which form to show based on user type
	const useLimitedForm = useMemo(() => {
		// Platform admins can only edit limited fields
		if (isPlatformAdmin) return true;
		// Org users with 3rd party auth can only edit limited fields
		if (isThirdPartyAuth) return true;
		// Org users with 1st party auth can edit full fields
		return false;
	}, [isPlatformAdmin, isThirdPartyAuth]);

	const handleEditClick = useCallback(() => {
		setIsEditing(true);
	}, []);

	const handleCancel = useCallback(() => {
		setIsEditing(false);
	}, []);

	const handleSuccess = useCallback(() => {
		setIsEditing(false);
	}, []);

	if (isLoading)
		return (
			<div className="flex items-center justify-center text-center h-[calc(100vh-16rem)]">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);

	if (hasError)
		return (
			<div className="flex flex-col items-center justify-center h-full gap-4">
				<Illustration type="cloud" size="sm">
					<div className="bg-error-500 rounded-full p-3">
						<AlertCircle className="size-6" />
					</div>
				</Illustration>
				<div className="flex flex-col items-center justify-center pt-4">
					<p className="text-gray-900 font-semibold text-md">
						{t("admin.organizations.errors.errorLoadingPage")}
					</p>
					<div className="w-[60%]">
						<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
							{t("admin.organizations.errors.errorLoadingContent")}
						</p>
					</div>
				</div>
			</div>
		);

	return (
		<div className="space-y-6">
			{/* Personal Details Section */}
			<div>
				<TabHeader
					title={t("userProfile.userDetails.tabTitle")}
					actions={
						!isEditing && (
							<ButtonUtility
								icon={Edit05}
								color="tertiary"
								size="sm"
								onClick={handleEditClick}
								tooltip={t("common.actions.edit")}
							/>
						)
					}
				/>

				<div className="bg-gray-50 rounded-lg p-4">
					<div className="space-y-4">
						{isEditing ? (
							// Show the appropriate form based on user type
							useLimitedForm ? (
								<LimitedProfileForm
									user={user!}
									onCancel={handleCancel}
									onSuccess={handleSuccess}
								/>
							) : (
								<FullProfileForm
									user={user!}
									onCancel={handleCancel}
									onSuccess={handleSuccess}
								/>
							)
						) : (
							// View mode
							<ViewMode
								user={user!}
								isPlatformAdmin={isPlatformAdmin}
								isThirdPartyAuth={isThirdPartyAuth}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
